import { GenericLocationItem } from '@api/map/types';
import { groupBy } from 'lodash';
import {
  CompanyForm,
  CompanyTypeOption,
  CreateCompanyRequest,
  InputCompanyDetail,
  UpdateCompanyRequest,
} from './company.types';
import { Option } from './fields/company-types/company-type-editor-v2';
import companyTypesMaster from 'assets/company-types';
import { getCompanyServices } from '../utils';

export const CompanyFormAdapter = {
  toForm(company: InputCompanyDetail): CompanyForm {
    const {
      company_types,
      company_subtypes,
      company_services,
      other_office_locations,
      other_offices_geocode,
      hq_geocode,
      hqcountry,
      hqcity,
      hqstate,
      social_urls,
      company_publish_ready,
      imdb_data = null,
      verified_sections = {
        profile_section: {},
        social_section: {},
        services_section: {},
        types_section: {},
        contact_section: {},
        attribute_section: {},
        metadata_section: {},
        badges_section: {},
        associated_person_section: {},
        media_section: {},
        policies_section: {},
      },
      ...body
    } = company;

    const addType =
      (type: 'type' | 'subtype') =>
      <T extends object>(d: T) => ({
        ...d,
        type,
      });

    const addLabel = <T extends CompanyTypeOption>(v: T) => ({ ...v, label: v.name, value: v.id });

    const clientServices = (company_services || [])
      .sort((as, bs) => as.order_id - bs.order_id)
      .map(v => ({ id: v.id, service: v.name, attributes_count:v.attributes_count, relation_vtr_id:v.relation_vtr_id, company_vtr_id:v.company_vtr_id,hierarchy_level:v.hierarchy_level,ultimate_parent:v.ultimate_parent}));
    const trim = (s: string) => (typeof s === 'string' ? s.trim() : '');
    const otherOfficesGeoCodes = (
      typeof other_offices_geocode === 'string' ? other_offices_geocode : ''
    )
      .split('|')
      .filter(trim)
      .map(g => g.split(',').map(trim))
      .map(([lat, lng]) => ({ lat: +lat, lng: +lng }))
      .filter(i => !isNaN(i.lat) && !isNaN(i.lng));

    /**
     * Converts a location string to city, country & state
     * object.
     * ### Example
     * ```ts
     * const location = arrayToLocation(['City', 'State', 'Country'])
     * // location = {city: 'City', state: 'State', country: 'Country'}
     *
     * const location = arrayToLocation(['City', 'Country'])
     * // location = {city: 'City', country: 'Country'}
     *
     * // Caveat
     * const location = arrayToLocation(['State', 'Country'])
     * // location = {city: 'State', country: 'Country'}
     * ```
     */
    const arrayToLocation = (location: string[]) => {
      if (location.length >= 3) {
        const [city = '', state = '', country = ''] = location;
        return {
          city,
          state,
          country,
        };
      } else if (location.length >= 2) {
        const [city = '', country = ''] = location;
        return { city, country };
      } else if (location.length === 1) {
        return { city: '', country: (location[0] || '').trim(), state: '' };
      }
      return { city: '', country: '', state: '' };
    };

    const otherOffices: GenericLocationItem[] = (
      typeof other_office_locations === 'string' ? other_office_locations : ''
    )
      .split('|')
      /**
       * Do not filter out empty values, we need proper
       * indexes on the next map(ofc, i). The value of i
       * will be incorrect if we filter out empty values.
       */
      // .filter(trim)
      .map(ofc => ofc.split(',').map(trim))
      .map((ofc, i) => ({ ...otherOfficesGeoCodes[i], ...arrayToLocation(ofc) }))
      /**
       * Filter out empty values after mapping.
       */
      .filter(({ country, city, state }) => country || city || state);

    const [hqLat = '', hqLng = ''] = (hq_geocode || '').split(',').map(trim);
    const getCord = (cord: string) => (cord && trim(cord) && !isNaN(+cord) ? +cord : null);
    const hqCode = { lat: getCord(hqLat), lng: getCord(hqLng) };
    const hqLocation: GenericLocationItem = {
      city: hqcity,
      country: hqcountry,
      state: hqstate,
      ...hqCode,
    };
    const [imdb_company_id, imdb_company_name, imdb_vtr_id, child_vtr_id] = imdb_data
      ? getIMDb(imdb_data)
      : [null, null, null, null];
    // const social_url= social_urls.replace(/[, ]+/g, ",").trim().replace(/,\s*$/, "");
    const social_urls_temp = social_urls
      ? social_urls.split(',').map(eachAka => eachAka.trim())
      : [];
    const nextCategories = company_types
      .sort((a, b) => a.order_id - b.order_id)
      .map(x => ({
        label: x.name,
        order_id: x.order_id,
        value: x.id,
      }));
    return {
      ...body,
      company_publish_ready:
        company_publish_ready === 0 || company_publish_ready === 1 ? 0 : company_publish_ready,
      categories: nextCategories,
      hqLocation,
      otherOffices,
      clientServices,
      imdb_company_id: imdb_company_id,
      imdb_company_name: imdb_company_name,
      imdb_vtr_id: imdb_vtr_id,
      child_vtr_id: child_vtr_id,
      social_urls: social_urls_temp as unknown as string,
      verified_sections,
    };
  },
  fromForm,
};
const getUpdatedServices =async (serviceIdsParam:string,
    sortedServices:any[])=>{
  return await getServices(serviceIdsParam,
    sortedServices)
}
export async function getServices(
  serviceIdsParam: string,
  sortedServices: any[]
) {
  return await getCompanyServices(
    serviceIdsParam,
    sortedServices
  ).toPromise();
}
function fromForm(details: CompanyForm): CreateCompanyRequest;
function fromForm(details: CompanyForm, compare: InputCompanyDetail): UpdateCompanyRequest;
function fromForm(details: CompanyForm, compare?: InputCompanyDetail): UpdateCompanyRequest {
  let {
    categories,
    hqLocation,
    otherOffices,
    logo,
    company_publish_ready,
    company_founding_year,
    clientServices,
    company_acquired_year,
    company_badges,
    imdb_company_id,
    imdb_company_name,
    imdb_vtr_id,
    social_urls,
    child_vtr_id,
    ...others
  } = details;
  /**
   * [Trim all string values.](https://vdovitrina-ai.atlassian.net/browse/VSDI-633)
   */
  const trimmedOthers = Object.fromEntries(
    Object.entries(others).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
  );

  /**
   * Build company data form.
   */
  let final: UpdateCompanyRequest = {
    ...trimmedOthers,
    ...(logo && logo !== 'deleted'
      ? { logo: { data: logo.split(',')[1] } }
      : logo === 'deleted'
      ? { logo: { data: null } }
      : null),
    company_publish_ready: +company_publish_ready,
    company_founding_year: company_founding_year ? +company_founding_year : null,
    company_acquired_year: company_acquired_year ? +company_acquired_year : null,
    hq_geocode: hqLocation
      ? `${hqLocation.lat ? hqLocation.lat : ''},${hqLocation.lng ? hqLocation.lng : ''}`
      : '',
    hqcountry: hqLocation?.country || '',
    hqcity: hqLocation?.city || '',
    hqstate: hqLocation?.state || '',
    other_offices_geocode:
      otherOffices?.map(o => `${o.lat ? o.lat : ''},${o.lng ? o.lng : ''}`).join('|') || '',
    other_office_locations:
      otherOffices
        ?.map(o =>
          [o.city, o.state, o.country].map(k => (k && k.trim() ? k.trim() : '')).join(', ')
        )
        .join('|') || '',
  };

  if (categories) {
    final.company_types = categories.map((t, idx) => ({
      is_deleted: false,
      id: t.value,
      order_id: idx + 1,
      name: t.label,
    }));
    final.company_subtypes = [];
  }
  // Finalize company_services
  final.company_services = (clientServices || []).map((c, idx) => ({
    id: c.id,
    name: c.service,
    is_deleted: false,
    order_id: idx + 1,
  }));
  const new_company_badges = company_badges ? JSON.parse(JSON.stringify(company_badges)) : [];
  new_company_badges.forEach(x => {
    delete x['badge_logo'];
    delete x['badge_subtype'];
    delete x['company_vtr_id'];
    delete x['order_id'];
    delete x['badge_name'];
    delete x['types'];

  });
  if (company_badges) final.company_badges = new_company_badges;
  if (imdb_company_id) {
    const imdb_data = setIMDb(imdb_company_id, imdb_company_name, imdb_vtr_id);
    final.imdb_data = imdb_data;
  }
  if (social_urls) {
    final.social_urls = (social_urls as unknown as Array<string>).filter(x => x).join(',');
  }
  return final;
}

function setIMDb(imdb_company_id, imdb_company_name, imdb_vtr_id) {
  var result = imdb_company_id.map((id, index) => {
    const imdb_temp_data = {
      imdb_company_id: id,
      imdb_company_name: imdb_company_name[index],
      vtr_id: imdb_vtr_id[index],
    };
    if (!imdb_vtr_id[index]) delete imdb_temp_data['vtr_id'];
    return imdb_temp_data;
  });

  result = result.filter(id => id.imdb_company_id !== null && id.imdb_company_id !== '');
  return result;
}
function getIMDb(imdb_data) {
  const imdb_company_id = imdb_data.map(e => e.imdb_company_id);
  const imdb_company_name = imdb_data.map(e => e.imdb_company_name);
  const imdb_vtr_id = imdb_data.map(e => e.vtr_id);
  const child_vtr_id = imdb_data.map(e => e.child_vtr_id);
  return [imdb_company_id, imdb_company_name, imdb_vtr_id, child_vtr_id];
}
